import { useRef, useState } from "react";
import { Engraved, Icon, Script } from "../components/ui";
import type { ScreenId } from "../components/ui";
import { FloralSpray } from "../components/Florals";
import type { FloralDensity } from "../components/Florals";
import { PhotoTile } from "../components/PhotoTile";
import type { LightboxTile } from "../components/Lightbox";
import { MOMENTS, SUGGESTED } from "../lib/content";
import { useEvent } from "../lib/event";
import { api } from "../lib/api";

interface SearchScreenProps {
  go: (s: ScreenId) => void;
  openMoment: (id: string) => void;
  density: FloralDensity;
  openLightbox: (tiles: LightboxTile[], index: number) => void;
}

interface ResolvedMoment {
  id: string;
  name: string;
  count: number;
}

interface ResultTile {
  key: string;
  seed?: number;
  src?: string;
  video?: boolean;
}

interface Submitted {
  text: string;
  moment: ResolvedMoment | null;
  tiles: ResultTile[];
}

export function SearchScreen({ go, openMoment, density, openLightbox }: SearchScreenProps) {
  const { eventId, live } = useEvent();
  const [q, setQ] = useState("");
  const [submitted, setSubmitted] = useState<Submitted | null>(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Mock keyword matcher — mirrors the prototype, used when not in live mode.
  const mockMatch = (query: string): Submitted => {
    const s = query.toLowerCase();
    let best: (typeof MOMENTS)[number] | null = null;
    let bestScore = 0;
    MOMENTS.forEach((m) => {
      let score = 0;
      m.kw.forEach((k) => {
        if (s.includes(k)) score += k.length;
      });
      if (s.includes(m.name.toLowerCase())) score += 20;
      if (score > bestScore) {
        bestScore = score;
        best = m;
      }
    });
    const m = best as (typeof MOMENTS)[number] | null;
    const tiles: ResultTile[] = m
      ? Array.from({ length: Math.min(m.count, 24) }, (_, i) => {
          const seed = m.count * 7 + i;
          return { key: String(seed), seed };
        })
      : [];
    return { text: query, moment: m ? { id: m.id, name: m.name, count: m.count } : null, tiles };
  };

  const run = async (query?: string) => {
    const text = (query ?? q).trim();
    if (!text) return;
    setQ(text);

    if (live && eventId) {
      setLoading(true);
      try {
        const r = await api.searchText({ eventId, query: text });
        setSubmitted({
          text,
          moment: r.moment ? { id: r.moment.key, name: r.moment.label, count: r.moment.photoCount } : null,
          tiles: r.results.map((p) => ({ key: p.photoId, src: p.thumbUrl, video: p.kind === "video" })),
        });
      } catch (err) {
        console.warn("text search failed; using mock:", err);
        setSubmitted(mockMatch(text));
      } finally {
        setLoading(false);
      }
      return;
    }

    setSubmitted(mockMatch(text));
  };

  const m = submitted?.moment ?? null;
  const tiles = submitted?.tiles ?? [];
  const lbTiles: LightboxTile[] = tiles.map((t) => ({ seed: t.seed, src: t.src, video: t.video }));

  return (
    <div className="screen search">
      <div className="search-top">
        <FloralSpray variant="cornerTR" density={density} className="search-corner" />
        <Engraved size={11.5} style={{ letterSpacing: "0.34em" }}>
          Ask The Album
        </Engraved>
        <Script size={42} className="search-title">
          Just ask
        </Script>
        <div className="search-field">
          <Icon name="search" size={19} stroke={1.6} />
          <input
            ref={inputRef}
            value={q}
            placeholder="Show me the first dance…"
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && run()}
          />
          {q && (
            <button
              className="search-clear"
              onClick={() => {
                setQ("");
                setSubmitted(null);
                inputRef.current?.focus();
              }}
              aria-label="Clear"
            >
              <Icon name="close" size={16} />
            </button>
          )}
        </div>
        <button className="btn primary wide" onClick={() => run()} disabled={!q.trim() || loading}>
          {loading ? "Searching…" : "Search the album"}
        </button>
      </div>

      {!submitted && (
        <div className="search-suggest">
          <Engraved size={11} style={{ color: "var(--c-ink-soft)" }}>
            Try asking for…
          </Engraved>
          <div className="chips">
            {SUGGESTED.map((s) => (
              <button key={s} className="chip" onClick={() => run(s)}>
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {submitted && m && (
        <div className="search-results">
          <div className="sr-head">
            <Engraved size={11} style={{ color: "var(--c-ink-soft)" }}>
              Found in your album
            </Engraved>
            <button className="mom-link" onClick={() => openMoment(m.id)}>
              <Script size={32} style={{ color: "var(--c-accent)" }}>
                {m.name}
              </Script>
              <span className="sr-count">
                {m.count} photos · open moment <Icon name="chevron" size={14} />
              </span>
            </button>
          </div>
          <div className="sr-grid">
            {tiles.map((t, i) => (
              <PhotoTile
                key={t.key}
                seed={t.seed}
                src={t.src}
                video={t.video}
                ratio="1 / 1"
                radius={5}
                onClick={() => openLightbox(lbTiles, i)}
              />
            ))}
          </div>
        </div>
      )}

      {submitted && !m && (
        <div className="search-empty">
          <FloralSpray variant="sprig" density={density} width={180} height={130} seed={19} />
          <p>No moments matched “{submitted.text}”. Try “first dance”, “sunset”, or “speeches”.</p>
        </div>
      )}
    </div>
  );
}
